import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import TbsLogo from "@/components/TbsLogo";
import { useI18n } from "@/i18n/I18nContext";

const Footer = () => {
  const { dict, t } = useI18n();
  const f = dict.footer;
  const year = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchor = (e, id) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const QUICK_LINKS = [
    { type: "anchor", id: "home", label: t("nav.home") },
    { type: "anchor", id: "about", label: t("nav.about") },
    { type: "route", to: "/ekibimiz", label: t("nav.team") },
    { type: "route", to: "/ihracat", label: t("nav.export") },
    { type: "route", to: "/teknoloji", label: t("nav.technology") },
    { type: "route", to: "/insaat", label: t("nav.construction") },
    { type: "route", to: "/insan-kaynaklari", label: t("nav.hr", "Careers") },
    { type: "anchor", id: "references", label: t("nav.references") },
    { type: "anchor", id: "contact", label: t("nav.contact") },
  ];

  const EMAILS = [
    { addr: "info@tbsgroup.com.tr", label: t("nav.contact", "Contact") },
    { addr: "ceo@tbsgroup.com.tr", label: "CEO" },
    { addr: "ceo.eu@tbsgroup.com.tr", label: "CEO · EU" },
  ];

  return (
    <footer
      data-testid="footer"
      className="relative bg-[hsl(var(--tbs-navy-950))] text-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <TbsLogo className="h-24 w-auto" />
          <p className="mt-5 max-w-sm text-sm text-white/75 leading-relaxed">
            {f.tagline}
          </p>
          <a
            href="mailto:info@tbsgroup.com.tr"
            className="mt-5 inline-flex items-center gap-2 text-sm text-white/85 hover:text-[hsl(var(--accent))]"
            data-testid="footer-email-link"
          >
            <Mail className="h-4 w-4" />
            info@tbsgroup.com.tr
          </a>

          <ul className="mt-4 space-y-1.5" data-testid="footer-emails">
            {EMAILS.filter((e) => e.addr !== "info@tbsgroup.com.tr").map((e) => (
              <li key={e.addr}>
                <a
                  href={`mailto:${e.addr}`}
                  className="inline-flex items-center gap-2 text-xs text-white/65 hover:text-[hsl(var(--accent))]"
                  data-testid={`footer-email-${e.addr}`}
                >
                  <span className="text-[hsl(var(--accent))] font-semibold text-[10px] tracking-[0.14em] uppercase">
                    {e.label}
                  </span>
                  <span>·</span>
                  <span>{e.addr}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
            {f.quickLinks}
          </div>
          <ul className="mt-4 space-y-2.5 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                {l.type === "route" ? (
                  <Link
                    to={l.to}
                    onClick={() => window.scrollTo({ top: 0, behavior: "auto" })}
                    className="text-white/75 hover:text-[hsl(var(--accent))]"
                    data-testid={`footer-link-${l.to.replace("/", "")}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    href={`#${l.id}`}
                    onClick={(e) => handleAnchor(e, l.id)}
                    className="text-white/75 hover:text-[hsl(var(--accent))]"
                    data-testid={`footer-link-${l.id}`}
                  >
                    {l.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
            {f.services}
          </div>
          <ul className="mt-4 space-y-2.5 text-sm">
            {dict.services.items.map((s) => (
              <li key={s.key}>
                <a
                  href="#services"
                  onClick={(e) => handleAnchor(e, "services")}
                  className="text-white/75 hover:text-[hsl(var(--accent))]"
                  data-testid={`footer-service-${s.key}`}
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <div data-testid="footer-copyright">
            {f.copyright.replace("{year}", year)}
          </div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-white" data-testid="footer-privacy">
              {f.privacy}
            </a>
            <a href="#" className="hover:text-white" data-testid="footer-terms">
              {f.terms}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
